import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import AdminLayout from '../../layouts/AdminLayout';
import Spinner from '../../components/common/Spinner';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';
import {
  getBankQuestions,
  createBankQuestion,
  updateBankQuestion,
  deleteBankQuestion,
  bulkUploadBankQuestions,
} from '../../api/admin.api';
import axiosInstance from '../../api/axiosInstance';

const downloadBankSample = async () => {
  const res = await axiosInstance.get('/admin/question-bank/sample', { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'sample_bank_questions.xlsx';
  a.click();
  window.URL.revokeObjectURL(url);
};

const TRUE_FALSE_OPTIONS = [
  { option_text: 'True', is_correct: false, order_index: 0 },
  { option_text: 'False', is_correct: false, order_index: 1 },
];

const MCQ_OPTIONS = [
  { option_text: '', is_correct: false, order_index: 0 },
  { option_text: '', is_correct: false, order_index: 1 },
  { option_text: '', is_correct: false, order_index: 2 },
  { option_text: '', is_correct: false, order_index: 3 },
];

const EMPTY_FORM = {
  question_text: '',
  question_type: 'mcq',
  marks: 1,
  explanation: '',
  tags: '',
  correct_text_answer: '',
  case_sensitive_match: false,
  options: MCQ_OPTIONS,
};

const QUESTION_TYPES = [
  { value: 'mcq', label: 'Multiple Choice (MCQ)' },
  { value: 'true_false', label: 'True / False' },
  { value: 'fill_in_the_blank', label: 'Fill in the Blank' },
  { value: 'short_answer', label: 'Short Answer (Manual Grading)' },
];

const inputCls = 'w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400';

const QuestionBankModal = ({ initial, onSave, onClose, saving, error }) => {
  const [form, setForm] = useState(initial || EMPTY_FORM);

  const handleTypeChange = (type) => {
    let options = form.options;
    if (type === 'true_false') options = TRUE_FALSE_OPTIONS;
    else if (type === 'mcq') options = MCQ_OPTIONS;
    else options = [];
    setForm({ ...form, question_type: type, options, correct_text_answer: '', case_sensitive_match: false });
  };

  const setOption = (i, field, val) => {
    const opts = form.options.map((o, idx) => idx === i ? { ...o, [field]: val } : o);
    setForm({ ...form, options: opts });
  };

  const markCorrect = (i) => {
    const opts = form.options.map((o, idx) => ({ ...o, is_correct: idx === i }));
    setForm({ ...form, options: opts });
  };

  const addOption = () =>
    setForm({ ...form, options: [...form.options, { option_text: '', is_correct: false, order_index: form.options.length }] });

  const removeOption = (i) =>
    setForm({ ...form, options: form.options.filter((_, idx) => idx !== i) });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      marks: parseFloat(form.marks) || 1,
      tags: form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : null,
    });
  };

  const isMcq = form.question_type === 'mcq';
  const isTrueFalse = form.question_type === 'true_false';
  const isFillBlank = form.question_type === 'fill_in_the_blank';
  const isShortAnswer = form.question_type === 'short_answer';

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/50">
      <div className="absolute inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl my-4">
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl z-10">
            <h2 className="text-lg font-semibold text-slate-900">{initial ? 'Edit Bank Question' : 'New Bank Question'}</h2>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">&times;</button>
          </div>

          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
            {error && <Alert type="error">{error}</Alert>}

            {/* Question Text */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Question Text</label>
              <textarea rows={3} className={inputCls} value={form.question_text}
                onChange={(e) => setForm({ ...form, question_text: e.target.value })} required />
            </div>

            {/* Question Type */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Question Type</label>
              <select className={inputCls} value={form.question_type} onChange={(e) => handleTypeChange(e.target.value)}>
                {QUESTION_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            {/* Marks + Tags */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Marks</label>
                <input type="number" min="0.5" step="0.5" className={inputCls} value={form.marks}
                  onChange={(e) => setForm({ ...form, marks: e.target.value })} required />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Tags (comma-separated)</label>
                <input type="text" className={inputCls} value={form.tags}
                  onChange={(e) => setForm({ ...form, tags: e.target.value })}
                  placeholder="e.g. javascript, functions" />
              </div>
            </div>

            {/* Explanation */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Explanation (optional)</label>
              <textarea rows={2} className={inputCls} value={form.explanation}
                onChange={(e) => setForm({ ...form, explanation: e.target.value })}
                placeholder="Shown after exam (if result view enabled)" />
            </div>

            {/* MCQ / True-False options */}
            {(isMcq || isTrueFalse) && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-slate-700">Answer Options</label>
                  {isMcq && (
                    <button type="button" onClick={addOption} className="text-xs text-primary-600 hover:underline">
                      + Add Option
                    </button>
                  )}
                </div>
                <p className="text-xs text-slate-400 mb-2">Click the radio button to mark the correct answer.</p>
                <div className="space-y-2">
                  {form.options.map((opt, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input type="radio" name="correct" checked={opt.is_correct}
                        onChange={() => markCorrect(i)}
                        className="w-4 h-4 accent-primary-600 flex-shrink-0" />
                      {isTrueFalse ? (
                        <span className="flex-1 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-700 bg-slate-50">
                          {opt.option_text}
                        </span>
                      ) : (
                        <input type="text" className={`${inputCls} flex-1`} value={opt.option_text}
                          onChange={(e) => setOption(i, 'option_text', e.target.value)}
                          placeholder={`Option ${i + 1}`} required />
                      )}
                      {isMcq && form.options.length > 2 && (
                        <button type="button" onClick={() => removeOption(i)}
                          className="text-slate-400 hover:text-red-500 text-lg leading-none flex-shrink-0">&times;</button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Fill in the blank */}
            {isFillBlank && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Correct Answer</label>
                  <input type="text" className={inputCls} value={form.correct_text_answer}
                    onChange={(e) => setForm({ ...form, correct_text_answer: e.target.value })}
                    placeholder="Enter the exact correct answer" required />
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.case_sensitive_match}
                    onChange={(e) => setForm({ ...form, case_sensitive_match: e.target.checked })}
                    className="w-4 h-4 accent-primary-600" />
                  <span className="text-sm text-slate-600">Case-sensitive matching</span>
                </label>
              </div>
            )}

            {/* Short answer info */}
            {isShortAnswer && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-700">
                Short answer responses require manual grading by the admin after exam submission.
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-2 pb-1">
              <Button type="submit" loading={saving} className="flex-1">Save Question</Button>
              <button type="button" onClick={onClose}
                className="flex-1 border border-slate-200 rounded-xl text-sm text-slate-500 hover:border-slate-300 py-2.5 transition-colors">
                Cancel
              </button>
            </div>
          </form>
        </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const QuestionBank = () => {
  const [questions, setQuestions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(null); // null | { mode: 'create' | 'edit', data?: obj }
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Bulk upload state
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const LIMIT = 20;

  const load = useCallback((p = page, s = search) => {
    setLoading(true);
    getBankQuestions({ search: s, page: p, limit: LIMIT })
      .then((res) => { setQuestions(res.data.rows); setTotal(res.data.total); })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    load(1, search);
  };

  const handleSave = async (formData) => {
    setSaving(true);
    setModalError('');
    try {
      if (modal.mode === 'create') {
        await createBankQuestion(formData);
      } else {
        await updateBankQuestion(modal.data.id, formData);
      }
      setModal(null);
      load(page, search);
    } catch (err) {
      setModalError(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteBankQuestion(id);
      setDeleteConfirm(null);
      load(page, search);
    } catch (err) {
      alert(err.response?.data?.message || 'Delete failed');
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) return;
    setBulkUploading(true);
    setBulkResult(null);
    try {
      const res = await bulkUploadBankQuestions(bulkFile);
      setBulkResult(res.data);
      setBulkFile(null);
      load(1, search);
      setPage(1);
    } catch (e) {
      setBulkResult({ success: false, inserted: 0, errors: [e.response?.data?.message || 'Upload failed'] });
    } finally {
      setBulkUploading(false);
    }
  };

  const pages = Math.ceil(total / LIMIT);

  return (
    <AdminLayout>
      <div className="mb-7 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Question Bank</h1>
          <p className="text-slate-500 text-sm mt-0.5">Reusable questions that can be added to any exam.</p>
        </div>
        <Button onClick={() => { setModal({ mode: 'create' }); setModalError(''); }}>
          + New Question
        </Button>
      </div>

      {/* ── Bulk Upload section ───────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-card p-6 mb-6">
        <h2 className="text-base font-semibold text-slate-800 mb-4">Bulk Upload Questions</h2>

        {/* Format guide */}
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 mb-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-blue-800 mb-2">Supported Question Types & Columns</p>
              <div className="overflow-x-auto">
                <table className="text-xs text-blue-700 w-full mb-2">
                  <thead>
                    <tr className="border-b border-blue-200">
                      <th className="text-left pb-1 font-semibold">Type</th>
                      <th className="text-left pb-1 font-semibold">Required Columns</th>
                      <th className="text-left pb-1 font-semibold">Optional Columns</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-blue-100">
                    <tr>
                      <td className="py-1 font-mono">mcq</td>
                      <td className="py-1">question_text, question_type, option_a, option_b, correct_option (A/B/C/D)</td>
                      <td className="py-1">option_c, option_d, marks, tags, explanation</td>
                    </tr>
                    <tr>
                      <td className="py-1 font-mono">true_false</td>
                      <td className="py-1">question_text, question_type, correct_option (True/False)</td>
                      <td className="py-1">marks, tags, explanation</td>
                    </tr>
                    <tr>
                      <td className="py-1 font-mono">fill_in_the_blank</td>
                      <td className="py-1">question_text, question_type, correct_answer</td>
                      <td className="py-1">case_sensitive (true/false), marks, tags, explanation</td>
                    </tr>
                    <tr>
                      <td className="py-1 font-mono">short_answer</td>
                      <td className="py-1">question_text, question_type</td>
                      <td className="py-1">marks, tags, explanation</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-blue-600">Download the sample file to see all 4 question types with examples.</p>
            </div>
            <button
              onClick={downloadBankSample}
              className="flex-shrink-0 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-4 py-2.5 rounded-xl transition-colors shadow-sm whitespace-nowrap"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download Sample
            </button>
          </div>
        </div>

        {/* File drop zone + upload button */}
        <div className="flex items-center gap-4">
          <label className={`flex-1 flex items-center justify-center h-14 border-2 border-dashed rounded-2xl cursor-pointer transition-colors ${
            bulkFile ? 'border-primary-400 bg-primary-50' : 'border-slate-300 bg-slate-50 hover:border-slate-400 hover:bg-slate-100'
          }`}>
            <input type="file" accept=".csv,.xlsx,.xls" className="hidden"
              onChange={(e) => { setBulkFile(e.target.files[0]); setBulkResult(null); }} />
            {bulkFile ? (
              <span className="text-sm font-medium text-primary-600">{bulkFile.name}</span>
            ) : (
              <span className="text-sm text-slate-500">Click to select .csv or .xlsx file</span>
            )}
          </label>
          <Button onClick={handleBulkUpload} loading={bulkUploading} disabled={!bulkFile}>
            Upload
          </Button>
          {bulkFile && (
            <button onClick={() => { setBulkFile(null); setBulkResult(null); }}
              className="text-slate-400 hover:text-slate-600 text-xl leading-none">&times;</button>
          )}
        </div>

        {/* Result */}
        {bulkResult && (
          <div className="mt-4 space-y-2">
            {bulkResult.inserted > 0 && (
              <Alert type="success">{bulkResult.inserted} question{bulkResult.inserted !== 1 ? 's' : ''} added to the bank successfully.</Alert>
            )}
            {bulkResult.errors?.length > 0 && (
              <Alert type="warning">
                {bulkResult.errors.length} row{bulkResult.errors.length !== 1 ? 's' : ''} had errors:
                <ul className="mt-1 list-disc list-inside text-xs space-y-0.5">
                  {bulkResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </Alert>
            )}
            {bulkResult.success === false && bulkResult.errors?.length === 0 && (
              <Alert type="error">Upload failed.</Alert>
            )}
          </div>
        )}
      </div>

      <form onSubmit={handleSearch} className="flex gap-2 mb-5">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search question text…"
          className="flex-1 border border-slate-200 rounded-xl px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400"
        />
        <Button type="submit">Search</Button>
        {search && (
          <button type="button" onClick={() => { setSearch(''); setPage(1); load(1, ''); }}
            className="px-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-500 hover:border-slate-300 transition-colors">
            Clear
          </button>
        )}
      </form>

      {loading ? (
        <Spinner className="py-20" />
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-card overflow-hidden">
          {questions.length === 0 ? (
            <p className="text-center py-14 text-slate-400">No questions in the bank yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Question</th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Type</th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Tags</th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Marks</th>
                  <th className="px-4 py-3.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {questions.map((q) => (
                  <tr key={q.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 max-w-xs">
                      <p className="text-sm text-slate-700 line-clamp-2">{q.question_text}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium ${
                        q.question_type === 'mcq' ? 'bg-blue-50 text-blue-700' :
                        q.question_type === 'true_false' ? 'bg-purple-50 text-purple-700' :
                        q.question_type === 'fill_in_the_blank' ? 'bg-amber-50 text-amber-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {q.question_type === 'mcq' ? 'MCQ' :
                         q.question_type === 'true_false' ? 'True/False' :
                         q.question_type === 'fill_in_the_blank' ? 'Fill Blank' : 'Short Answer'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {q.tags?.length ? (
                        <div className="flex flex-wrap gap-1">
                          {q.tags.map((t) => (
                            <span key={t} className="px-2 py-0.5 bg-slate-100 rounded-full text-xs text-slate-600">{t}</span>
                          ))}
                        </div>
                      ) : <span className="text-slate-400 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{q.marks}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => {
                            setModal({
                              mode: 'edit',
                              data: { ...q, tags: q.tags?.join(', ') || '' },
                            });
                            setModalError('');
                          }}
                          className="text-xs text-primary-600 hover:underline"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(q.id)}
                          className="text-xs text-red-500 hover:underline"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {pages > 1 && (
            <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between">
              <p className="text-xs text-slate-400">{total} total questions</p>
              <div className="flex gap-1">
                <button disabled={page === 1} onClick={() => { const p = page - 1; setPage(p); load(p); }}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs disabled:opacity-40 hover:border-slate-300">Prev</button>
                <span className="px-3 py-1.5 text-xs text-slate-500">{page} / {pages}</span>
                <button disabled={page === pages} onClick={() => { const p = page + 1; setPage(p); load(p); }}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs disabled:opacity-40 hover:border-slate-300">Next</button>
              </div>
            </div>
          )}
        </div>
      )}

      {modal && (
        <QuestionBankModal
          initial={modal.data}
          onSave={handleSave}
          onClose={() => setModal(null)}
          saving={saving}
          error={modalError}
        />
      )}

      {deleteConfirm && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Delete Question?</h3>
            <p className="text-sm text-slate-500 mb-5">This cannot be undone. If this question is used in an exam, deletion will be blocked.</p>
            <div className="flex gap-3">
              <button onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 bg-red-600 text-white rounded-xl py-2.5 text-sm font-semibold hover:bg-red-700 transition-colors">
                Delete
              </button>
              <button onClick={() => setDeleteConfirm(null)}
                className="flex-1 border border-slate-200 rounded-xl py-2.5 text-sm text-slate-500 hover:border-slate-300 transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </AdminLayout>
  );
};

export default QuestionBank;
